/**
 * Composant de paramètres des notifications push
 * Permet aux utilisateurs de configurer leurs préférences de notification
 */
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Bell, BellOff, Volume2, VolumeX, Eye, EyeOff, MessageSquare, AtSign, Heart, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import {
    isPushSupported,
    getNotificationSettings,
    saveNotificationSettings,
    requestNotificationPermission,
    initializePushNotifications,
    showLocalNotification
} from '@/lib/pushNotifications';

export default function NotificationSettings({ compact = false }) {
    const [settings, setSettings] = useState(getNotificationSettings());
    const [permissionStatus, setPermissionStatus] = useState('default');
    const [loading, setLoading] = useState(false);
    
    useEffect(() => {
        if (isPushSupported()) {
            setPermissionStatus(Notification.permission);
        }
    }, []);
    
    const handleToggleEnabled = async (enabled) => {
        setLoading(true);
        
        if (enabled) {
            // Activer les notifications
            if (Notification.permission !== 'granted') {
                const permission = await requestNotificationPermission();
                setPermissionStatus(permission);
                
                if (permission !== 'granted') {
                    toast.error('Vous devez autoriser les notifications dans votre navigateur');
                    setLoading(false);
                    return;
                }
            }
            
            await initializePushNotifications();
        }
        
        const newSettings = { ...settings, enabled };
        setSettings(newSettings);
        saveNotificationSettings(newSettings);
        toast.success(enabled ? 'Notifications activées' : 'Notifications désactivées');
        setLoading(false);
    };
    
    const handleToggleSetting = (key, value) => {
        const newSettings = { ...settings, [key]: value };
        setSettings(newSettings);
        saveNotificationSettings(newSettings);
    };
    
    const handleToggleChannel = (channel, value) => {
        const newSettings = {
            ...settings,
            channels: {
                ...settings.channels,
                [channel]: value
            }
        };
        setSettings(newSettings);
        saveNotificationSettings(newSettings);
    };
    
    const handleTestNotification = async () => {
        if (!settings.enabled || Notification.permission !== 'granted') {
            toast.error('Activez d\'abord les notifications');
            return;
        }
        
        await showLocalNotification({
            title: 'Test ValNexo',
            body: 'Les notifications fonctionnent correctement ! 🎉',
            channelType: 'chat'
        });
        toast.success('Notification de test envoyée');
    };
    
    const getPermissionBadge = () => {
        if (!isPushSupported()) {
            return <Badge variant="outline" className="text-slate-500">Non supporté</Badge>;
        }
        
        switch (permissionStatus) {
            case 'granted':
                return <Badge className="bg-green-100 text-green-700">Autorisé</Badge>;
            case 'denied':
                return <Badge className="bg-red-100 text-red-700">Bloqué</Badge>;
            default:
                return <Badge variant="outline" className="text-yellow-600">Non demandé</Badge>;
        }
    };
    
    // Version compacte pour le widget
    if (compact) {
        return (
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2">
                    {settings.enabled ? (
                        <Bell className="w-4 h-4 text-yellow-500" />
                    ) : (
                        <BellOff className="w-4 h-4 text-slate-400" />
                    )}
                    <span className="text-sm font-medium">Notifications</span>
                </div>
                <Switch
                    checked={settings.enabled && permissionStatus === 'granted'}
                    onCheckedChange={handleToggleEnabled}
                    disabled={loading || permissionStatus === 'denied'}
                />
            </div>
        );
    }
    
    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <Bell className="w-5 h-5 text-yellow-500" />
                    Notifications Push
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
                {/* Statut permission */}
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                    <div>
                        <p className="font-medium">Statut navigateur</p>
                        <p className="text-sm text-slate-500">Permission pour les notifications</p>
                    </div>
                    {getPermissionBadge()}
                </div>
                
                {permissionStatus === 'denied' && (
                    <div className="p-4 bg-red-50 rounded-xl text-sm text-red-700">
                        <p className="font-medium mb-1">Notifications bloquées</p>
                        <p>Vous avez bloqué les notifications. Pour les activer, cliquez sur l'icône de cadenas dans la barre d'adresse de votre navigateur.</p>
                    </div>
                )}
                
                {/* Toggle principal */}
                <div className="flex items-center justify-between p-4 border rounded-xl">
                    <div className="flex items-center gap-3">
                        {settings.enabled ? (
                            <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                                <Bell className="w-5 h-5 text-yellow-600" />
                            </div>
                        ) : (
                            <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                                <BellOff className="w-5 h-5 text-slate-400" />
                            </div>
                        )}
                        <div>
                            <p className="font-medium">Activer les notifications</p>
                            <p className="text-sm text-slate-500">Recevez des alertes pour les nouveaux messages</p>
                        </div>
                    </div>
                    <Switch
                        checked={settings.enabled && permissionStatus === 'granted'}
                        onCheckedChange={handleToggleEnabled}
                        disabled={loading || permissionStatus === 'denied'}
                    />
                </div>
                
                {/* Options avancées */}
                {settings.enabled && permissionStatus === 'granted' && (
                    <>
                        <div className="space-y-4">
                            <h4 className="font-medium text-slate-700">Options</h4>
                            
                            {/* Son */}
                            <div className="flex items-center justify-between py-2">
                                <div className="flex items-center gap-3">
                                    {settings.sound ? (
                                        <Volume2 className="w-5 h-5 text-slate-500" />
                                    ) : (
                                        <VolumeX className="w-5 h-5 text-slate-400" />
                                    )}
                                    <Label htmlFor="sound">Son de notification</Label>
                                </div>
                                <Switch
                                    id="sound"
                                    checked={settings.sound}
                                    onCheckedChange={(v) => handleToggleSetting('sound', v)}
                                />
                            </div>
                            
                            {/* Aperçu */}
                            <div className="flex items-center justify-between py-2">
                                <div className="flex items-center gap-3">
                                    {settings.showPreview ? (
                                        <Eye className="w-5 h-5 text-slate-500" />
                                    ) : (
                                        <EyeOff className="w-5 h-5 text-slate-400" />
                                    )}
                                    <Label htmlFor="preview">Afficher l'aperçu du message</Label>
                                </div>
                                <Switch
                                    id="preview"
                                    checked={settings.showPreview}
                                    onCheckedChange={(v) => handleToggleSetting('showPreview', v)}
                                />
                            </div>
                        </div>
                        
                        {/* Types de notifications */}
                        <div className="space-y-4">
                            <h4 className="font-medium text-slate-700">Types de notifications</h4>
                            
                            <div className="flex items-center justify-between py-2">
                                <div className="flex items-center gap-3">
                                    <MessageSquare className="w-5 h-5 text-blue-500" />
                                    <Label htmlFor="chat">Messages du chat</Label>
                                </div>
                                <Switch
                                    id="chat"
                                    checked={settings.channels?.chat !== false}
                                    onCheckedChange={(v) => handleToggleChannel('chat', v)}
                                />
                            </div>
                            
                            <div className="flex items-center justify-between py-2">
                                <div className="flex items-center gap-3">
                                    <AtSign className="w-5 h-5 text-green-500" />
                                    <Label htmlFor="mentions">Mentions (@vous)</Label>
                                </div>
                                <Switch
                                    id="mentions"
                                    checked={settings.channels?.mentions !== false}
                                    onCheckedChange={(v) => handleToggleChannel('mentions', v)}
                                />
                            </div>
                            
                            <div className="flex items-center justify-between py-2">
                                <div className="flex items-center gap-3">
                                    <Heart className="w-5 h-5 text-red-500" />
                                    <Label htmlFor="reactions">Réactions à vos posts</Label>
                                </div>
                                <Switch
                                    id="reactions"
                                    checked={settings.channels?.reactions !== false}
                                    onCheckedChange={(v) => handleToggleChannel('reactions', v)}
                                />
                            </div>
                        </div>
                        
                        {/* Test */}
                        <div className="pt-4 border-t">
                            <Button 
                                variant="outline" 
                                onClick={handleTestNotification}
                                className="w-full"
                            >
                                <RefreshCw className="w-4 h-4 mr-2" />
                                Tester les notifications
                            </Button>
                        </div>
                    </>
                )}
            </CardContent>
        </Card>
    );
}
